import React, { Fragment, useCallback, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, ThemeProvider, Typography } from "@mui/material";
import { useEffectAsync } from "../../../../../common/hooks/use-effect-async";
import { ValidateOperationDataCommandHandler } from "../cqrs/validate-operation-data-command/validate-operation-data-command-handler";
import { Done, ErrorOutline, Replay } from "@mui/icons-material";
import { useTimeout } from "../../../../../common/hooks/use-timeout";
import { notificationModel } from "../../../../../common/component/notifications/notification-model";
import { VPUITheme } from "../../../../../common/theme/vpui-theme";
import { t } from "i18next";

import "./style.scss";

interface IOperationDataValidationPopupProps {
    operation: string;
    onClose: () => void;
}

export const OperationDataValidationPopup = (props: IOperationDataValidationPopupProps) => {
    const [isRunning, setIsRunning] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isErrorMessage, setIsErrorMessage] = useState(false);

    const handleSuccess = useCallback(() => {
        setIsCompleted(true); setIsErrorMessage(false); setIsRunning(false);
    }, []);

    const handleError = useCallback(() => {
        setIsCompleted(false); setIsErrorMessage(true); setIsRunning(false);
    }, []);

    const handleRetry = useCallback(() => {
        setIsCompleted(false); setIsErrorMessage(false); setIsRunning(true);
    }, []);

    const handleValidationComplete = useCallback(() => {
        notificationModel.addSuccessMessage(t("components.operationDataValidation.validate.success", {
            operationCode: props.operation,
        }));
        props.onClose();
    }, []);

    useTimeout(handleValidationComplete, 1500, isCompleted);

    return <ThemeProvider theme={VPUITheme}>
        <Dialog open={true} fullWidth>
            {isRunning ?
                <ValidationRunner operation={props.operation} onComplete={handleSuccess} onError={handleError} /> :
                !isErrorMessage ?
                    <Success operation={props.operation} /> :
                    <Error operation={props.operation} onRetry={handleRetry} />}
        </Dialog>
    </ThemeProvider>;
};

const ValidationRunner =
    ({ operation, onComplete, onError }:
     { operation: string, onComplete: () => void, onError: () => void },
    ) => {
    useEffectAsync(async () => {
        try {
            await ValidateOperationDataCommandHandler(operation);

            onComplete();
        } catch (err) {
            onError();
        }
    }, []);

    return <DialogContent className={"validation-popup"}>
        <CircularProgress size={"10vw"} />
        <Typography>
            {t("components.operationDataValidation.validate.progress", { operationCode: operation })}
        </Typography>
    </DialogContent>;
};

const Success =
    ({ operation }:
     { operation: string },
    ) => {
    return <DialogContent className={"validation-popup"}>
        <div className={"validation-popup__icon"}>
            <Done color={"success"} />
        </div>
        <Typography>
            {t("components.operationDataValidation.validate.success", { operationCode: operation })}
        </Typography>
    </DialogContent>;
};

const Error =
    ({ onRetry, operation }:
     { onRetry: () => void, operation: string },
    ) => {
    return <Fragment>
        <DialogContent className={"validation-popup"}>
            <div className={"validation-popup__icon"}>
                <ErrorOutline color={"error"} />
            </div>
            <Typography>
                {t("components.operationDataValidation.validate.error", { operationCode: operation })}
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button variant={"contained"} color={"primary"} endIcon={<Replay />} onClick={onRetry}>
                {t("components.operationDataValidation.validate.retryButton")}
            </Button>
        </DialogActions>
    </Fragment>;
};
